import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useZh } from '../lib/useProduct';

// Shared footer: the tagline plus the legal links. These sit on the auth pages
// specifically because that is where someone is about to hand over an email
// address and a CV — the point at which they are entitled to read the policy.
export function LegalFooter() {
  const zh = useZh();
  return (
    <>
      {zh ? '专为英国华人求职打造' : 'Local. Private. Yours.'}
      {' · '}
      <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>
        {zh ? '隐私政策' : 'Privacy'}
      </Link>
      {' · '}
      <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>
        {zh ? '服务条款' : 'Terms'}
      </Link>
    </>
  );
}

// Minimal layout shared by Login / Register / Forgot / Reset / VerifyEmail.
// Centred card, brand header, footer link slot.
export default function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ padding: '22px 48px', borderBottom: '1px solid var(--line)' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'inherit', textDecoration: 'none' }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--ink)', color: '#fff', display: 'grid', placeItems: 'center', fontFamily: 'Fraunces, var(--cjk-serif), serif', fontWeight: 700, fontSize: 14 }}>N</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Nice2Job</div>
        </Link>
      </header>

      <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '48px 24px' }}>
        <div style={{ maxWidth: 380, width: '100%' }}>
          <h1 className="serif" style={{ fontSize: 28, lineHeight: 1.1, margin: '0 0 8px', fontWeight: 400 }}>{title}</h1>
          {subtitle && <p style={{ color: 'var(--ink2)', margin: '0 0 28px', fontSize: 14, lineHeight: 1.5 }}>{subtitle}</p>}
          {children}
          {footer && <div style={{ marginTop: 24, fontSize: 13, color: 'var(--ink2)' }}>{footer}</div>}
        </div>
      </div>

      <footer style={{ padding: '20px 48px', borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink3)', textAlign: 'center' }}>
        <LegalFooter />
      </footer>
    </div>
  );
}

// Tiny shared form pieces — keep things consistent without dragging in a UI lib.
export function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      {children}
      {error && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{error}</div>}
    </label>
  );
}

export const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid var(--line)',
  borderRadius: 6,
  background: '#fff',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
